import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0, title: '' });

  // SEO meta data by language
  const seoData = {
    sk: {
      title: 'Galéria',
      description: 'Galéria realizovaných projektov montáže slnečných ochranných fólií. Rodinné domy, komerčné priestory, autofólie.',
      keywords: 'galéria fólie, realizácie fólie, projekty montáž fólií, fotky fólií'
    },
    cs: {
      title: 'Galerie',
      description: 'Galerie realizovaných projektů montáže slunečních ochranných fólií. Rodinné domy, komerční prostory.',
      keywords: 'galerie fólie, realizace fólie, projekty montáž fólií'
    },
    en: {
      title: 'Gallery',
      description: 'Gallery of completed window film installation projects. Residential homes, commercial spaces, automotive films.',
      keywords: 'film gallery, film projects, installation portfolio, film photos'
    },
    de: {
      title: 'Galerie',
      description: 'Galerie realisierter Sonnenschutzfolien-Projekte. Einfamilienhäuser, Gewerberäume, Autofolien.',
      keywords: 'Folien Galerie, Folien Projekte, Montage Portfolio'
    },
    ru: {
      title: 'Галерея',
      description: 'Галерея выполненных проектов по монтажу солнцезащитных пленок. Частные дома, коммерческие помещения, автопленки.',
      keywords: 'галерея пленки, проекты пленки, портфолио монтажа'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.sk;

  const categories = [
    { id: 'all', label: t('gallery.all') },
    { id: 'residential', label: t('gallery.residential') },
    { id: 'commercial', label: t('gallery.commercial') },
    { id: 'automotive', label: t('gallery.automotive') }
  ];

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const url = selectedCategory === 'all'
          ? `${API_URL}/api/gallery`
          : `${API_URL}/api/gallery?category=${selectedCategory}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setItems(data);
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [selectedCategory]);

  const getTranslation = (item) => {
    const lang = getUiLang(i18n);
    return item.translations[lang] || item.translations['sk'] || item.translations['en'];
  };

  const openLightbox = (item, imageIndex = 0) => {
    const translation = getTranslation(item);
    setLightbox({
      open: true,
      images: item.images,
      index: imageIndex,
      title: translation.title
    });
  };

  const closeLightbox = () => {
    setLightbox({ open: false, images: [], index: 0, title: '' });
  };

  const nextImage = () => {
    setLightbox(prev => ({
      ...prev,
      index: (prev.index + 1) % prev.images.length
    }));
  };

  const prevImage = () => {
    setLightbox(prev => ({
      ...prev,
      index: prev.index === 0 ? prev.images.length - 1 : prev.index - 1
    }));
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightbox.open) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.open]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords={currentSeo.keywords}
        url="/gallery"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="gallery-title">
            {t('gallery.title')}
          </h1>
          <p className="text-xl opacity-90">{t('gallery.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full transition ${
                selectedCategory === cat.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-orange-100'
              }`}
              data-testid={`gallery-category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {t('gallery.no_items')}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(item => {
              const translation = getTranslation(item);
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer group"
                  onClick={() => openLightbox(item)}
                  data-testid="gallery-item"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={item.images[0] || 'https://via.placeholder.com/400x300'}
                      alt={translation.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                    {item.images.length > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-sm">
                        +{item.images.length - 1} {t('gallery.photos')}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-1">{translation.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{translation.description}</p>
                    <span className="inline-block mt-2 px-2 py-1 bg-orange-100 text-orange-600 rounded text-xs">
                      {item.category}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox.open && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition"
            onClick={closeLightbox}
          >
            <X className="h-8 w-8" />
          </button>
          
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition p-2"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition p-2"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight className="h-10 w-10" />
          </button>

          <div className="max-w-5xl max-h-[80vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.images[lightbox.index]}
              alt={lightbox.title}
              className="max-w-full max-h-[75vh] object-contain mx-auto"
            />
            <div className="text-center mt-4">
              <h3 className="text-white text-xl font-bold">{lightbox.title}</h3>
              <p className="text-gray-400 mt-1">
                {lightbox.index + 1} / {lightbox.images.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
